import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { ErrorCircleFilled, Crop20Regular, Image16Regular } from '@fluentui/react-icons';
import { Button, mergeClasses } from '@fluentui/react-components';
import { TooltipHost } from '@bingads-webui-react/fabric-control';

import { AdPreviewPlacementContainerBase } from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

import { usePlacementContaierStyles, usePlacementContainerV2Styles } from './styles';

const tooltipProps = {
  maxWidth: '200px',
};

export const PlacementContainer = ({
  aspectRatioUIString,
  previewTypeString,
  minSize,
  isRequired,
  isValid,
  onCrop,
  children, // the preview content
  i18n,
}) => {
  const classes = usePlacementContaierStyles();
  const onRenderHeader = () => (
    <>
      <div className={classes.containerTitle}>
        <div className={classes.previewWrapper}>
          <Image16Regular />
          <div className={classnames(classes.ratio, 'placement-ratio')}>{aspectRatioUIString}</div>
          <div className={classnames(classes.previewType, 'preview-type')}>{i18n.getString(previewTypeString)}</div>
        </div>
        {
          isRequired &&
          <div className={classnames(classes.requireBox, !isValid && classes.errRequireBox, 'require-mode')}>
            {i18n.getString(_TL_('Required'))}
          </div>
        }
        {
          !isValid &&
          <TooltipHost
            content={i18n.getString(_TL_("The image doesn't meet the minimum size required for this ad placement."))}
            tooltipProps={tooltipProps}
          >
            <ErrorCircleFilled className={classnames(classes.errorIcon, 'error-icon')} />
          </TooltipHost>
        }
      </div>
      <Button
        className={classnames('crop-button', classes.cropButton)}
        onClick={onCrop}
        disabled={!isValid}
        aria-label={i18n.getString(_TL_('Crop image'))}
      >
        <Crop20Regular />
      </Button>
    </>
  );

  const onRenderFooter = () => !isValid && (
    <div className={classnames(classes.minSize, 'error-info')}>
      {i18n.getString(_TL_('Minimum: {{width}} x {{height}} pixels'), {
        width: minSize[0],
        height: minSize[1],
      })}
    </div>
  );

  return (
    <AdPreviewPlacementContainerBase
      isValid={isValid}
      onRenderHeader={onRenderHeader}
      onRenderFooter={onRenderFooter}
      previewContainerClassName={mergeClasses('ad-preview-component-container', classes.adPreviewComponentContainer)}
    >
      {children}
    </AdPreviewPlacementContainerBase>
  );
};

PlacementContainer.propTypes = {
  aspectRatioUIString: PropTypes.string.isRequired,
  previewTypeString: PropTypes.string.isRequired,
  minSize: PropTypes.arrayOf(PropTypes.number).isRequired,
  isRequired: PropTypes.bool,
  isValid: PropTypes.bool,
  onCrop: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

PlacementContainer.defaultProps = {
  isRequired: false,
  isValid: true,
};

export const PlacementContainerV2 = ({
  aspectRatioUIString,
  previewTypeString,
  minSize,
  isRequired,
  isValid,
  onCrop,
  children, // the preview content
  i18n,
}) => {
  const classes = usePlacementContainerV2Styles();
  const onRenderHeader = () => (
    <>
      <div className={classes.containerTitle}>
        <div className={classnames(classes.ratio, 'placement-ratio')}>{aspectRatioUIString}</div>
        <div className={classnames(classes.previewType, 'preview-type')}>{i18n.getString(previewTypeString)}</div>
        {
          !isValid &&
          <TooltipHost
            content={i18n.getString(_TL_("The image doesn't meet the minimum size required for this ad placement."))}
            tooltipProps={tooltipProps}
          >
            <ErrorCircleFilled className={classnames(classes.errorIcon, 'error-icon')} />
          </TooltipHost>
        }
      </div>
      <div className={classes.rightBox}>
        {
          isRequired &&
          <div className={classnames(classes.requireBox, !isValid && classes.errRequireBox, 'require-mode')}>
            {i18n.getString(_TL_('Required'))}
          </div>
        }
        <Button
          className={classnames('crop-button', classes.cropButton)}
          onClick={onCrop}
          disabled={!isValid}
          aria-label={i18n.getString(_TL_('Edit'))}
        >
          {i18n.getString(_TL_('Edit'))}
        </Button>
      </div>
    </>
  );

  const renderMinSize = () => (
    <div className={classnames(classes.minSize, 'min-size-info')}>
      {i18n.getString(_TL_('Minimum dimensions: {{width}} x {{height}}'), {
        width: minSize[0],
        height: minSize[1],
      })}
    </div>
  );

  return (
    <AdPreviewPlacementContainerBase
      isValid={isValid}
      onRenderHeader={onRenderHeader}
      containerHeaderClassName={classes.containerHeader}
      previewContainerClassName={mergeClasses('ad-preview-component-container', classes.adPreviewComponentContainer)}
      placementContainerClassName={classes.adPreviewPlacementContainer}
    >
      {renderMinSize()}
      <div className={classes.cardBox}>{children}</div>
    </AdPreviewPlacementContainerBase>
  );
};

PlacementContainerV2.propTypes = {
  aspectRatioUIString: PropTypes.string.isRequired,
  previewTypeString: PropTypes.string.isRequired,
  minSize: PropTypes.arrayOf(PropTypes.number).isRequired,
  isRequired: PropTypes.bool,
  isValid: PropTypes.bool,
  onCrop: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

PlacementContainerV2.defaultProps = {
  isRequired: false,
  isValid: true,
};
